import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  GeneralLedgerTransaction,
  Response,
  Document,
  DocumentStatus,
  DocumentType,
  DocumentComment,
  AccountBalance,
  AccountType,
  ChangeLog,
  Pagination,
} from '@ui/src';
import { transactionApi } from './services';
import { Channel, CofProps, Employee, CASP } from '../type';
import { glTransactionsListApi } from '../../general-ledger/store/services';
import { Balance } from '../../../types/account';

export interface TransactionsState {
  expandedGLTransaction?: GeneralLedgerTransaction;
  expandedGLTransactionDebitBalance?: AccountBalance;
  expandedGLTransactionCreditBalance?: AccountBalance;
  employee?: { id: string; profile: { firstName: string; lastName: string } };
  chats?: Channel[];
  companyOfficials?: CofProps[];
  currentEmployee?: Employee;
  documents?: Document[];
  document?: Document;
  documentsPagination?: Pagination;
  accessToken?: string;
  balances?: Balance[];
  caspList?: CASP[];
  transactionChangeLogs?: ChangeLog[];
  transactionChangeLogsPagination?: Pagination;
  transactionComments?: DocumentComment[];
  transactionCommentsPagination?: Pagination;
}

const initialState: TransactionsState = {};

export const transactionsSlice = createSlice({
  name: 'internalList',
  initialState,
  reducers: {
    setAccessToken: (state, action: PayloadAction<string>) => {
      state.accessToken = action.payload;
    },
    clearExpandedGeneralLedgerTransactionState: (state) => {
      state.expandedGLTransaction = undefined;
    },
    clearDocumentState: (state) => {
      state.document = undefined;
    },
    clearState: (state) => {
      state.chats = undefined;
      state.companyOfficials = undefined;
      state.balances = undefined;
      state.transactionChangeLogs = undefined;
      state.transactionChangeLogsPagination = undefined;
      state.documentsPagination = undefined;
      state.documents = undefined;
      state.transactionComments = undefined;
      state.transactionCommentsPagination = undefined;
    },
  },
  extraReducers: (builder) => {
    builder.addMatcher(
      transactionApi.endpoints.getTransactionsEmployee.matchFulfilled,
      (
        state,
        action: PayloadAction<{
          data: {
            id: string;
            profile: { firstName: string; lastName: string };
          };
        }>,
      ) => {
        if (!state.employee) {
          state.employee = action.payload.data;
        }
      },
    );
    builder.addMatcher(
      transactionApi.endpoints.getGeneralLedgerExpandedTransaction
        .matchFulfilled,
      (state, action: PayloadAction<{ data: GeneralLedgerTransaction }>) => {
        state.expandedGLTransaction = action.payload.data;
      },
    );
    builder.addMatcher(
      transactionApi.endpoints.getTransactionComments.matchFulfilled,
      (
        state,
        action: PayloadAction<Response<DocumentComment>> & {
          meta: {
            arg: {
              originalArgs: {
                cursor?: string;
              };
            };
          };
        },
      ) => {
        let comments;

        if (!action.meta?.arg?.originalArgs?.cursor) {
          comments = action.payload.data.edges?.map((item) => item.node);
        } else {
          comments = ([] as DocumentComment[]).concat(
            state.transactionComments as DocumentComment[],
          );

          action.payload.data.edges?.forEach((item) =>
            comments.push(item.node),
          );
        }

        state.transactionComments = comments;
        state.transactionCommentsPagination = action.payload.data.pageInfo;
      },
    );
    builder.addMatcher(
      transactionApi.endpoints.getAllAccountalances.matchFulfilled,
      (state, action: PayloadAction<Response<Balance>>) => {
        state.balances = action.payload.data.edges?.map((item) => item.node);
      },
    );
    builder.addMatcher(
      transactionApi.endpoints.addCommentToTransaction.matchFulfilled,
      (state, action: PayloadAction<{ data: DocumentComment }>) => {
        let comments: DocumentComment[] = [];

        if (state.transactionComments) {
          comments = ([] as DocumentComment[]).concat(
            state.transactionComments,
          );
        }

        comments.unshift(action.payload.data);

        state.transactionComments = comments;
      },
    );
    builder.addMatcher(
      transactionApi.endpoints.editCommentInTransaction.matchFulfilled,
      (state, action: PayloadAction<{ data: DocumentComment }>) => {
        state.transactionComments = state.transactionComments?.map((item) => {
          const result = { ...item };

          if (result.id === item.id) {
            result.text = action.payload.data.text;
          }

          return result;
        });
      },
    );
    builder.addMatcher(
      transactionApi.endpoints.deleteCommentInTransaction.matchFulfilled,
      (state, action: PayloadAction<{ data: DocumentComment }>) => {
        const nextTranscationComments = state?.transactionComments?.filter(
          (item) => item.id !== action.payload.data.id,
        );

        state.transactionComments = nextTranscationComments;
      },
    );
    builder.addMatcher(
      transactionApi.endpoints.getTransactionChats.matchFulfilled,
      (state, action: PayloadAction<Response<Channel>>) => {
        state.chats = action.payload.data.edges?.map((item) => item.node);
      },
    );
    builder.addMatcher(
      transactionApi.endpoints.getCurrentEmployee.matchFulfilled,
      (state, action: PayloadAction<{ data: Employee }>) => {
        state.currentEmployee = action.payload.data;
      },
    );
    builder.addMatcher(
      transactionApi.endpoints.getClientsOfficias.matchFulfilled,
      (state, action: PayloadAction<Response<CofProps>>) => {
        state.companyOfficials = action.payload.data.edges?.map(
          (item) => item.node,
        );
      },
    );
    builder.addMatcher(
      transactionApi.endpoints.getTransactionsDocuments.matchFulfilled,
      (
        state,
        action: PayloadAction<Response<Document>> & {
          meta: {
            arg: {
              originalArgs: {
                cursor?: string;
              };
            };
          };
        },
      ) => {
        let documents;

        if (!action.meta?.arg?.originalArgs?.cursor) {
          documents = action.payload.data.edges?.map((item) => item.node);
        } else {
          documents = ([] as Document[]).concat(state.documents as Document[]);

          action.payload.data.edges?.forEach((item) =>
            documents.push(item.node),
          );
        }

        state.documents = documents;
        state.documentsPagination = action.payload.data.pageInfo;
      },
    );
    builder.addMatcher(
      transactionApi.endpoints.getTransactionsCASP.matchFulfilled,
      (state, action: PayloadAction<Response<CASP>>) => {
        state.caspList = action?.payload?.data?.edges?.map((item) => item.node);
      },
    );
    builder.addMatcher(
      transactionApi.endpoints.createTransactionChat.matchFulfilled,
      (state, action: PayloadAction<{ data: Channel }>) => {
        let nextChats: Channel[] = [];

        if (state.chats) {
          nextChats = ([] as Channel[]).concat(state.chats);
        }

        nextChats.unshift(action.payload.data);

        state.chats = nextChats;
      },
    );
    builder.addMatcher(
      transactionApi.endpoints.addDocumentToTransaction.matchFulfilled,
      (state, action: PayloadAction<{ data: Document }>) => {
        const nextDocuments = ([] as Document[]).concat(
          state.documents as Document[],
        );

        nextDocuments.unshift(action.payload.data);

        state.documents = nextDocuments;
      },
    );
    builder.addMatcher(
      transactionApi.endpoints.getTransactionDocumentById.matchFulfilled,
      (state, action: PayloadAction<{ data: Document }>) => {
        const nextDocument = { ...action.payload.data };

        state.document = {
          ...nextDocument,
          commentsList: action.payload.data.comments?.edges.map(
            (item) => item.node,
          ),
        };
      },
    );
    builder.addMatcher(
      transactionApi.endpoints.changeDocumentStatus.matchFulfilled,
      (
        state,
        action: {
          meta: {
            arg: {
              originalArgs: {
                body: {
                  ids: string[];
                  status: DocumentStatus;
                };
              };
            };
          };
        },
      ) => {
        const { ids, status } = action.meta.arg.originalArgs.body;

        const nextDocuments = state?.documents?.map((item) => {
          const result = { ...item };
          const currentDocumentId = ids.find((id) => result.id === id);

          if (result.id === currentDocumentId) {
            result.status = status;
          }

          return result;
        });

        if (state.document) {
          const nextDocument = { ...state.document };

          if (ids.find((item) => item === nextDocument.id)) {
            nextDocument.status = status;

            state.document = nextDocument;
          }
        }

        state.documents = nextDocuments;
      },
    );
    builder.addMatcher(
      transactionApi.endpoints.changeDocumentCategory.matchFulfilled,
      (
        state,
        action: {
          meta: {
            arg: {
              originalArgs: {
                body: {
                  ids: string[];
                  category: DocumentType;
                };
              };
            };
          };
        },
      ) => {
        const { ids, category } = action.meta.arg.originalArgs.body;

        const nextDocuments = state?.documents?.map((item) => {
          const result = { ...item };
          const currentDocumentId = ids.find((id) => result.id === id);

          if (result.id === currentDocumentId) {
            result.category = category;
          }

          return result;
        });

        if (state.document) {
          const nextDocument = { ...state.document };

          if (ids.find((item) => item === nextDocument.id)) {
            nextDocument.category = category;

            state.document = nextDocument;
          }
        }

        state.documents = nextDocuments;
      },
    );
    builder.addMatcher(
      transactionApi.endpoints.deleteDocuments.matchFulfilled,
      (
        state,
        action: {
          meta: {
            arg: {
              originalArgs: {
                body: {
                  ids: string[];
                };
              };
            };
          };
        },
      ) => {
        const { ids } = action.meta.arg.originalArgs.body;

        const nextDocuments = state?.documents?.filter((item) =>
          ids.every((id) => item.id !== id),
        );

        state.document = undefined;

        state.documents = nextDocuments;
      },
    );
    builder.addMatcher(
      transactionApi.endpoints.addComment.matchFulfilled,
      (state, action: PayloadAction<{ data: DocumentComment }>) => {
        if (state.document) {
          const nextDocument = { ...state.document };
          let nextComments: DocumentComment[] = [];

          if (
            nextDocument.commentsList &&
            nextDocument.commentsList.length > 0
          ) {
            nextComments = ([] as DocumentComment[]).concat(
              nextDocument.commentsList,
            );
          }

          nextComments.push(action.payload.data);

          nextDocument.commentsList = nextComments;

          state.document = nextDocument;
        }
      },
    );
    builder.addMatcher(
      transactionApi.endpoints.editComment.matchFulfilled,
      (state, action: PayloadAction<{ data: DocumentComment }>) => {
        if (state.document) {
          const nextDocument = { ...state.document };
          nextDocument.commentsList = nextDocument.commentsList?.map((item) => {
            let result = { ...item };

            if (result.id === action.payload.data.id) {
              result = action.payload.data;
            }

            return result;
          });

          state.document = nextDocument;
        }
      },
    );
    builder.addMatcher(
      transactionApi.endpoints.getTransactionChangeLogs.matchFulfilled,
      (
        state,
        action: PayloadAction<Response<ChangeLog>> & {
          meta: { arg: { originalArgs: string } };
        },
      ) => {
        if (!action.meta.arg.originalArgs.includes('cursor')) {
          state.transactionChangeLogs = action.payload.data?.edges?.map(
            (item) => item.node,
          );
        } else {
          const nextChangeLogs = ([] as ChangeLog[]).concat(
            state.transactionChangeLogs as ChangeLog[],
          );

          action.payload.data.edges?.forEach((item) => {
            nextChangeLogs.push(item.node);
          });

          state.transactionChangeLogs = nextChangeLogs;
        }

        state.transactionChangeLogsPagination = action.payload.data?.pageInfo;
      },
    );
    builder
      .addMatcher(
        transactionApi.endpoints.deleteComment.matchFulfilled,
        (state, action: PayloadAction<{ data: DocumentComment }>) => {
          if (state.document) {
            const nextDocument = { ...state.document };

            nextDocument.commentsList = nextDocument?.commentsList?.filter(
              (item) => item.id !== action.payload.data.id,
            );

            state.document = nextDocument;
          }
        },
      )
      .addMatcher(
        glTransactionsListApi.endpoints.getTransactionsAccountBalanceById
          .matchFulfilled,
        (
          state,
          action: PayloadAction<Response<AccountBalance>> & {
            meta: {
              arg: {
                originalArgs: {
                  id: string;
                  query?: string | undefined;
                  typeAcc: AccountType;
                };
              };
            };
          },
        ) => {
          if (
            action.meta.arg.originalArgs.typeAcc &&
            state.expandedGLTransaction
          ) {
            const [accBalance] = action.payload.data.edges.map(
              (item) => item.node,
            );

            if (action.meta.arg.originalArgs.typeAcc === AccountType.DEBIT) {
              state.expandedGLTransactionDebitBalance = accBalance;
            } else if (
              action.meta.arg.originalArgs.typeAcc === AccountType.CREDIT
            ) {
              state.expandedGLTransactionCreditBalance = accBalance;
            }
          }
        },
      );
  },
});

export const {
  clearExpandedGeneralLedgerTransactionState,
  clearState,
  clearDocumentState,
  setAccessToken,
} = transactionsSlice.actions;

export default transactionsSlice.reducer;
